import { useEffect, useImperativeHandle, forwardRef, useState } from "react";
import { useSelector } from "../../store";
import { Alert, Box, Grid2, Typography } from "@mui/material";
import { useForm, SubmitHandler } from "react-hook-form";
import FormTextField from "../forms/FormTextField";
import { useSkeletonLoader } from "../../hooks/useSkeletonLoader";
import FormPhoneField from "../forms/FormPhoneField";
import CompanySettings from "../../types/companySettings";
import FormCheckbox from "../forms/FormCheckbox";
import FormDropzone from "../forms/FormDropzone";
import CompanyLogo from "../base/CompanyLogo";
import { Dictionary } from "@reduxjs/toolkit";
import Option from "../../types/option";
import SsoForm from "./SsoForm";

const ROLE_CLAIM_ATTR_KEY = "custom:idp_roles" as const;

const STATIC_ATTR_KEYS = [
  "email",
  "email_verified",
  "username",
  "family_name",
  "given_name",
  ROLE_CLAIM_ATTR_KEY,
] as const;

type StaticAttrKey = (typeof STATIC_ATTR_KEYS)[number];

export type RoleAssignmentMode = "default" | "mapped";

type RoleMappingField = {
  matchValue: string;
  role?: Option | null;
  roleId?: number;
};

const buildStaticAttributes = (
  source?: Dictionary<string>
): Record<StaticAttrKey, string> =>
  STATIC_ATTR_KEYS.reduce<Record<StaticAttrKey, string>>((acc, key) => {
    acc[key] = source?.[key] ?? "";
    return acc;
  }, {} as Record<StaticAttrKey, string>);

const buildRoleMappingFields = (
  mappings?: Option[],
  availableRoles?: Option[]
): RoleMappingField[] =>
  (mappings ?? []).map((mapping) => {
    const matchedRole = availableRoles?.find((role) => role.id === mapping?.id);
    return {
      matchValue: mapping?.value ?? "",
      role: matchedRole ?? null,
      roleId: mapping?.id,
    };
  });

const roleMappingsToOptions = (fields?: RoleMappingField[]): Option[] =>
  (fields ?? [])
    .map((field) => {
      const roleId = field.role?.id ?? field.roleId;
      const matchValue = field.matchValue?.trim();
      if (!roleId || !matchValue) return null;
      return { id: roleId, value: matchValue };
    })
    .filter((mapping): mapping is Option => mapping !== null);

interface Props {
  onSave: SubmitHandler<CompanySettings>;
}

export type SettingsFormSubmitHandler = () => void;

export type SettingsFormValues = CompanySettings & {
  ssoSettings?: CompanySettings["ssoSettings"] & {
    mappedAttributes?: Record<StaticAttrKey, string>;
    roleMappingsList?: RoleMappingField[];
  };
};

const stripExtraAttributes = (
  settings: SettingsFormValues["ssoSettings"] | undefined,
  roleMode: RoleAssignmentMode = "default"
): CompanySettings["ssoSettings"] | undefined => {
  if (!settings) return undefined;
  const {
    mappedAttributes = buildStaticAttributes(),
    roleMappingsList,
    defaultRoles,
    ...rest
  } = settings;

  return {
    ...rest,
    defaultRoles: roleMode === "default" ? defaultRoles : undefined,
    mappedRoles:
      roleMode === "mapped"
        ? roleMappingsToOptions(roleMappingsList)
        : undefined,
    mappedAttributes,
  } as CompanySettings["ssoSettings"];
};

const SettingsForm = forwardRef<SettingsFormSubmitHandler, Props>(
  (props, ref) => {
    const { onSave } = props;

    const { selectedCompany, status, allowedCompanies } = useSelector(
      (store) => store.company
    );
    const { awsUser: loggedInUser } = useSelector((store) => store.auth);
    const { userRoles } = useSelector((store) => store.user);

    const existingMap =
      selectedCompany?.settings?.ssoSettings?.mappedAttributes ?? undefined;
    const staticAttributes = buildStaticAttributes(existingMap);
    const roleMappingDefaults = buildRoleMappingFields(
      selectedCompany?.settings?.ssoSettings?.mappedRoles,
      userRoles || []
    );

    const [roleMode, setRoleMode] = useState<RoleAssignmentMode>(() =>
      (selectedCompany?.settings?.ssoSettings?.mappedRoles?.length ?? 0) > 0
        ? "mapped"
        : "default"
    );

    const { handleSubmit, control, watch, reset, setValue } =
      useForm<SettingsFormValues>({
        mode: "onChange",
        defaultValues: {
          ...selectedCompany?.settings,
          contactEmail:
            selectedCompany?.settings?.contactEmail ??
            loggedInUser?.signInDetails?.loginId,
          useSso: !!selectedCompany?.settings?.useSso,
          ssoSettings: {
            ...selectedCompany?.settings?.ssoSettings,
            mappedAttributes: staticAttributes,
            roleMappingsList: roleMappingDefaults,
          },
        },
      });

    const formValues = watch();

    const { loadWithSkeleton } = useSkeletonLoader(status === "loading");

    const submit: SubmitHandler<SettingsFormValues> = (values) => {
      const { ssoSettings, ...companyRest } = values;
      onSave({
        ...(companyRest as CompanySettings),
        ssoSettings: stripExtraAttributes(ssoSettings, roleMode),
      });
    };

    useImperativeHandle(ref, () => handleSubmit(submit));

    useEffect(() => {
      if (!selectedCompany?.settings || !selectedCompany?.settings?.useSso)
        return;

      const mappedAttributes =
        selectedCompany.settings.ssoSettings?.mappedAttributes;
      const existingRoleMappings =
        selectedCompany.settings.ssoSettings?.mappedRoles;

      reset({
        ...selectedCompany.settings,
        ssoSettings: {
          ...selectedCompany.settings.ssoSettings,
          mappedAttributes: buildStaticAttributes(mappedAttributes),
          roleMappingsList: buildRoleMappingFields(
            existingRoleMappings,
            userRoles || []
          ),
        },
      });

      setRoleMode(
        (existingRoleMappings?.length ?? 0) > 0 ? "mapped" : "default"
      );
    }, [reset, selectedCompany?.settings, userRoles]);

    useEffect(() => {
      if (!formValues.useSso) {
        setValue("ssoSettings", undefined);
        return;
      }

      if (!formValues.ssoSettings?.mappedAttributes) {
        setValue("ssoSettings.mappedAttributes", buildStaticAttributes());
      }
    }, [formValues.ssoSettings?.mappedAttributes, formValues.useSso, setValue]);

    return (
      <Box display="flex" flexDirection="row" width="100%">
        <Box display="flex" flexDirection="column" width="100%">
          <form>
            <Grid2 container spacing={2} paddingBottom={2}>
              <Box display="flex" flexDirection="row" width="100%" marginY={1}>
                <Typography fontWeight="bold">Company Information</Typography>
              </Box>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                {loadWithSkeleton(
                  <FormTextField
                    label="Company Name"
                    control={control}
                    fieldName="companyName"
                    required
                  />
                )}
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                {loadWithSkeleton(
                  <FormTextField
                    control={control}
                    fieldName="contactEmail"
                    label="Contact Email"
                    required
                    type="email"
                  />
                )}
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                {loadWithSkeleton(
                  <FormPhoneField
                    control={control}
                    fieldName="contactPhone"
                    label="Contact Phone"
                    required
                  />
                )}
              </Grid2>
              <Box display="flex" flexDirection="row" width="100%" marginY={1}>
                <Typography fontWeight="bold">Company Logo</Typography>
              </Box>
              <Grid2 size={12}>
                {loadWithSkeleton(
                  <FormDropzone
                    control={control}
                    fieldName={"reportLogo.upload"}
                  />
                )}
              </Grid2>
              {!!selectedCompany?.settings?.reportLogo &&
                !formValues?.reportLogo?.upload && (
                  <Grid2 size={12}>
                    <CompanyLogo company={selectedCompany} size="100px" />
                  </Grid2>
                )}
              {!!selectedCompany?.finishedSetup && (
                <>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    {loadWithSkeleton(
                      <FormCheckbox
                        control={control}
                        fieldName="useSso"
                        label="Use SSO to manage authentication?"
                        disabled={allowedCompanies?.length > 1}
                      />
                    )}
                    {allowedCompanies?.length > 1 && (
                      <Alert
                        severity="info"
                        sx={{
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        Single sign-on can only be implemented for one company
                        at a time. Create a new single-tenant account to set up
                        SSO.
                      </Alert>
                    )}
                  </Grid2>
                </>
              )}
              {!!selectedCompany?.finishedSetup && !!formValues.useSso && (
                <SsoForm
                  control={control}
                  formValues={formValues}
                  setValue={setValue}
                  roleMode={roleMode}
                  onRoleModeChange={setRoleMode}
                />
              )}
            </Grid2>
          </form>
        </Box>
      </Box>
    );
  }
);

export default SettingsForm;
